import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatDateTime, formatRelative } from '@/lib/formatters';
import type { User } from '@/types';
import { PropertyGroup, PropertyRow } from './PropertyList';
import { bags, fieldAccessOf, profileGaps, type ProfileGap } from './profileModel';
import {
  age,
  country,
  date,
  humanize,
  isFuture,
  maskAccount,
  money,
  relativeDays,
  tenure,
  text,
} from './profileFormat';

/**
 * How complete the profile is, and exactly what's missing — so a sparse record
 * reads as a short to-do list rather than a wall of empty fields.
 */
export function CompletenessStrip({
  user,
  onFill,
}: {
  user: User;
  /** Omit when the viewer can't edit; the strip then only reports. */
  onFill?: (tab: ProfileGap['tab']) => void;
}): React.ReactElement | null {
  const { total, missing } = profileGaps(user);
  if (missing.length === 0) return null;
  const filled = total - missing.length;
  const percent = Math.round((filled / total) * 100);
  return (
    <div className="rounded-xl border border-amber-200/80 bg-amber-50/60 px-4 py-3 dark:border-amber-500/20 dark:bg-amber-500/5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Profile {percent}% complete
          <span className="ml-2 font-normal text-gray-600 dark:text-gray-400">
            {missing.length} {missing.length === 1 ? 'detail' : 'details'} missing
          </span>
        </p>
        {onFill ? (
          <button
            type="button"
            onClick={() => onFill(missing[0].tab)}
            className="shrink-0 cursor-pointer rounded-md text-sm font-medium text-amber-800 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 dark:text-amber-300"
          >
            Fill in
          </button>
        ) : null}
      </div>
      <div
        className="mt-2 h-1 overflow-hidden rounded-full bg-amber-100 dark:bg-amber-500/10"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Profile completeness"
      >
        <div className="h-full rounded-full bg-amber-500" style={{ width: `${percent}%` }} />
      </div>
      <p className="mt-2 text-xs leading-5 text-gray-600 dark:text-gray-400">
        {missing.map((gap, index) => (
          <span key={gap.label}>
            {onFill ? (
              <button
                type="button"
                onClick={() => onFill(gap.tab)}
                className="cursor-pointer rounded underline decoration-gray-300 underline-offset-2 hover:text-gray-900 hover:decoration-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50 dark:decoration-gray-600 dark:hover:text-gray-100"
              >
                {gap.label}
              </button>
            ) : (
              gap.label
            )}
            {index < missing.length - 1 ? ', ' : ''}
          </span>
        ))}
      </p>
    </div>
  );
}

export function OverviewView({
  user,
  roleDisplay,
}: {
  user: User;
  roleDisplay: string;
}): React.ReactElement {
  const { personalInfo } = bags(user);
  const { sensitive } = fieldAccessOf(user);
  return (
    <div className="space-y-7">
      <PropertyGroup title="Personal">
        <PropertyRow
          label="Date of birth"
          value={date(personalInfo.dateOfBirth)}
          hint={age(personalInfo.dateOfBirth)}
          hidden={!sensitive}
        />
        <PropertyRow label="Gender" value={humanize(personalInfo.gender)} />
        <PropertyRow label="Nationality" value={country(personalInfo.nationality)} />
        <PropertyRow label="Marital status" value={humanize(personalInfo.maritalStatus)} />
        <PropertyRow
          label="CNIC number"
          value={text(personalInfo.cnicNumber)}
          mono
          hidden={!sensitive}
        />
        <PropertyRow
          label="Passport number"
          value={text(personalInfo.passportNumber)}
          mono
          hidden={!sensitive}
        />
      </PropertyGroup>

      <PropertyGroup title="Account">
        <PropertyRow
          label="Work email"
          value={
            <a
              href={`mailto:${user.email}`}
              className="text-gray-900 underline-offset-4 hover:text-primary hover:underline dark:text-gray-100"
            >
              {user.email}
            </a>
          }
        />
        <PropertyRow label="Employee ID" value={text(user.employeeId)} mono />
        <PropertyRow label="Role" value={text(roleDisplay)} />
        <PropertyRow
          label="Member since"
          value={date(user.createdAt)}
          hint={tenure(user.createdAt)}
        />
        <PropertyRow
          label="Last sign-in"
          value={
            user.lastLoginAt ? (
              <span title={formatDateTime(user.lastLoginAt)}>
                {formatRelative(user.lastLoginAt)}
              </span>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">Never signed in</span>
            )
          }
        />
      </PropertyGroup>
    </div>
  );
}

/** One dated step in someone's employment, oldest first. */
function Milestone({
  label,
  value,
  detail,
  upcoming,
  tone = 'default',
  last,
}: {
  label: string;
  value: string;
  detail: string | null;
  upcoming: boolean;
  tone?: 'default' | 'danger';
  last: boolean;
}): React.ReactElement {
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      {!last ? (
        <span
          aria-hidden
          className="absolute left-[5px] top-4 h-[calc(100%-0.5rem)] w-px bg-gray-200 dark:bg-gray-700"
        />
      ) : null}
      <span
        aria-hidden
        className={cn(
          'relative mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full border-2',
          upcoming
            ? 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
            : tone === 'danger'
              ? 'border-red-500 bg-red-500'
              : 'border-primary bg-primary'
        )}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {value}
          {detail ? <span className="ml-2 text-xs text-gray-500">{detail}</span> : null}
        </p>
      </div>
    </li>
  );
}

export function EmploymentView({ user }: { user: User }): React.ReactElement {
  const { employment } = bags(user);
  const milestones = [
    {
      key: 'joined',
      label: 'Joined',
      raw: employment.joiningDate,
      detail: tenure(employment.joiningDate) ? `${tenure(employment.joiningDate)} ago` : null,
    },
    {
      key: 'probation',
      label: isFuture(employment.probationEndDate) ? 'Probation ends' : 'Probation ended',
      raw: employment.probationEndDate,
      detail: relativeDays(employment.probationEndDate),
    },
    {
      key: 'confirmed',
      label: 'Confirmed permanent',
      raw: employment.confirmationDate,
      detail: null,
    },
    {
      key: 'exit',
      label: 'Exited',
      raw: employment.exitDate,
      detail: text(employment.exitReason),
      tone: 'danger' as const,
    },
  ].filter((step) => date(step.raw));

  return (
    <div className="space-y-7">
      <PropertyGroup title="Position">
        <PropertyRow label="Department" value={text(employment.department)} />
        <PropertyRow label="Designation" value={text(employment.designation)} />
        <PropertyRow label="Employment type" value={humanize(employment.employmentType)} />
        <PropertyRow label="Work location" value={humanize(employment.workLocation)} />
      </PropertyGroup>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Timeline</h3>
        {milestones.length > 0 ? (
          <ol>
            {milestones.map((step, index) => (
              <Milestone
                key={step.key}
                label={step.label}
                value={date(step.raw) ?? ''}
                detail={step.detail}
                upcoming={isFuture(step.raw)}
                tone={step.tone}
                last={index === milestones.length - 1}
              />
            ))}
          </ol>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No employment dates recorded yet. Add a joining date to start the timeline.
          </p>
        )}
      </section>
    </div>
  );
}

export function PayView({
  user,
  fallbackCurrency,
}: {
  user: User;
  fallbackCurrency: string;
}): React.ReactElement {
  const { compensation, bank, tax } = bags(user);
  const { sensitive, payRates } = fieldAccessOf(user);
  const [revealAccount, setRevealAccount] = useState(false);
  const currency = text(compensation.currency) ?? fallbackCurrency;
  const isHourly = compensation.compensationType === 'hourly';
  const rate = money(isHourly ? compensation.hourlyRate : compensation.baseSalary, currency);
  const account = text(bank.accountNumber);

  return (
    <div className="space-y-7">
      <div className="rounded-xl border border-gray-200 px-5 py-4 dark:border-gray-700">
        {!payRates ? (
          <>
            <p className="text-base font-medium text-gray-900 dark:text-gray-100">
              Pay rate hidden
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              You don't have permission to view this person's pay.
            </p>
          </>
        ) : rate ? (
          <>
            <p className="text-2xl font-semibold tabular-nums tracking-tight text-gray-900 dark:text-gray-100">
              {rate}
              <span className="ml-1 text-base font-normal text-gray-500 dark:text-gray-400">
                {isHourly ? '/ hour' : '/ month'}
              </span>
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {[
                humanize(compensation.compensationType),
                humanize(compensation.payFrequency) &&
                  `Paid ${humanize(compensation.payFrequency)?.toLowerCase()}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </>
        ) : (
          <>
            <p className="text-base font-medium text-gray-900 dark:text-gray-100">
              No pay rate set
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Payroll can't include this person until a salary or hourly rate is recorded.
            </p>
          </>
        )}
      </div>

      <PropertyGroup title="Bank">
        <PropertyRow label="Bank" value={text(bank.bankName)} hidden={!sensitive} />
        <PropertyRow
          label="Account number"
          hidden={!sensitive}
          value={
            account ? (
              <span className="inline-flex items-center gap-2 tabular-nums">
                {revealAccount ? account : maskAccount(account)}
                <button
                  type="button"
                  onClick={() => setRevealAccount((shown) => !shown)}
                  className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                  aria-label={revealAccount ? 'Hide account number' : 'Show account number'}
                >
                  {revealAccount ? (
                    <EyeOff className="h-3.5 w-3.5" />
                  ) : (
                    <Eye className="h-3.5 w-3.5" />
                  )}
                </button>
              </span>
            ) : null
          }
        />
      </PropertyGroup>

      <PropertyGroup title="Tax">
        <PropertyRow label="Tax number" value={text(tax.taxNumber)} mono hidden={!sensitive} />
        <PropertyRow label="Tax bracket" value={text(tax.taxBracket)} hidden={!sensitive} />
        <PropertyRow
          label="Tax exempt"
          value={typeof tax.taxExempt === 'boolean' ? text(tax.taxExempt) : null}
          hidden={!sensitive}
        />
      </PropertyGroup>
    </div>
  );
}

export function ContactView({ user }: { user: User }): React.ReactElement {
  const { contactInfo, address, emergency } = bags(user);
  const phone = text(contactInfo.phone);
  const altPhone = text(contactInfo.alternatePhone);
  const personalEmail = text(contactInfo.personalEmail);
  const cityLine = [text(address.city), text(address.state), text(address.postalCode)]
    .filter(Boolean)
    .join(', ');
  const addressLines = [text(address.street), cityLine || null, country(address.country)].filter(
    Boolean
  );
  const link = 'underline-offset-4 hover:text-primary hover:underline';

  return (
    <div className="space-y-7">
      <PropertyGroup title="Reach">
        <PropertyRow
          label="Phone"
          value={
            phone ? (
              <a className={link} href={`tel:${phone}`}>
                {phone}
              </a>
            ) : null
          }
          mono
        />
        <PropertyRow
          label="Alternate phone"
          value={
            altPhone ? (
              <a className={link} href={`tel:${altPhone}`}>
                {altPhone}
              </a>
            ) : null
          }
          mono
        />
        <PropertyRow
          label="Personal email"
          value={
            personalEmail ? (
              <a className={link} href={`mailto:${personalEmail}`}>
                {personalEmail}
              </a>
            ) : null
          }
        />
        <PropertyRow
          label="Address"
          value={
            addressLines.length > 0 ? (
              <span className="block leading-6">
                {addressLines.map((line) => (
                  <span key={line} className="block">
                    {line}
                  </span>
                ))}
              </span>
            ) : null
          }
        />
      </PropertyGroup>

      <PropertyGroup title="Emergency contact">
        <PropertyRow label="Name" value={text(emergency.name)} />
        <PropertyRow label="Relationship" value={humanize(emergency.relationship)} />
        <PropertyRow
          label="Phone"
          value={
            text(emergency.phone) ? (
              <a className={link} href={`tel:${text(emergency.phone)}`}>
                {text(emergency.phone)}
              </a>
            ) : null
          }
          mono
        />
        <PropertyRow label="Email" value={text(emergency.email)} />
      </PropertyGroup>
    </div>
  );
}
